import React, { PropsWithChildren, useState } from 'react';

import styled from 'styled-components';

import { Activity } from '../../../types/Activity';

import ActivityViewActionIcon from './ActivityViewActionIcon';

/* eslint-disable no-shadow */

const StyledInput = styled.input`
  border: 1px solid #ccc;
  border-radius: 4px;
  color: #555555;
`;

const ValidationError = styled.div`
  color: #f00;
`;

type Props = {
  activity: Activity;
  fieldsInEditMode: string[];
  max?: number;
  maxLength?: number;
  min?: number;
  name: string;
  onCancel: (name: string) => void;
  onEditField: (name: string) => void;
  onSave: (name: string, value: string | number) => void;
  style?: React.CSSProperties;
  type?: string;
};

const getInputType = (type?: string): string => {
  if (!type) return 'text';
  if (type === 'number') return 'text';
  return type;
};

const ActivityViewEditableField = ({
  activity,
  children,
  fieldsInEditMode,
  max = 500,
  maxLength = 500,
  min = 0,
  name,
  onCancel,
  onEditField,
  onSave,
  style,
  type,
}: PropsWithChildren<Props>) => {
  const [value, setValue] = useState<string | number>(activity[name]);
  const [error, setError] = useState<string>('');

  const onCancelClick = () => {
    setValue(activity[name]);
    onCancel(name);
  };

  const onChange = event => {
    setValue(event.target.value);

    if (type === 'number') {
      const parsed = Number(event.target.value);
      if (Number.isNaN(parsed)) {
        setError('Value should be a number');
        return;
      }

      if (parsed < min || max < parsed) {
        setError(`Number should be in range between ${min} and ${max}`);
        return;
      }
    } else if (type === 'text') {
      if (event.target.value?.toString().length > maxLength) {
        setError(`Length should not exceed ${maxLength} characters`);
        return;
      }
    }

    setError('');
  };

  if (fieldsInEditMode.includes(name))
    return (
      <>
        <StyledInput
          style={style}
          type={getInputType(type)}
          value={value}
          onChange={onChange}
        />
        <ActivityViewActionIcon
          action='save'
          onClick={() =>
            onSave(
              name,
              type === 'text' ? value.toString().trim() : Number(value)
            )
          }
          disabled={!!error}
        />
        <ActivityViewActionIcon action='ban' onClick={onCancelClick} />
        {error && <ValidationError>{error}</ValidationError>}
      </>
    );
  return (
    <>
      {children}
      <ActivityViewActionIcon action='edit' onClick={() => onEditField(name)} />
    </>
  );
};

export default React.memo(ActivityViewEditableField);
